import { ChevronRight, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Card, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Etiqueta, Tabla } from "../../components/ui/primitivos";
import { fetchImpresora } from "../../lib/consola-api";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import { ESTADOS_IMPRESORA, EstadoImpresoraBadge } from "./estado";
import { agruparOrdenes, type OrdenDeImpresora } from "./impresora";

/**
 * La ficha de una impresora, porteada de `PrinterDetailPage.tsx` (ADR 0066).
 *
 * **Es una pantalla y no un modal** (ADR 0064), y lo que cambia no es solo el
 * tamaño: el modal mostraba **una sola lista**, la de OTs activas. Allá el
 * trabajo se reparte en tres bloques —lo que se está haciendo, lo que espera
 * turno y lo que ya se cerró—, que es la pregunta que uno trae cuando abre una
 * máquina.
 *
 * **Sin `refetchInterval`.** `PrinterDetailPage` refresca cada 30 s; acá eso
 * está prohibido (ADR 0006, invariante 11) porque impide que Neon suspenda. En
 * su lugar queda el botón «Actualizar», que allá también está.
 */
export function ImpresoraPagina() {
  const { id = "" } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const { can } = useSession();

  const detalle = useDatos(() => fetchImpresora(id), id);

  if (detalle.error) {
    return (
      <div className="space-y-6">
        <PageHeader titulo="Impresora" alVolver={() => navegar("/impresoras")} />
        <Aviso>{detalle.error}</Aviso>
      </div>
    );
  }

  const datos = detalle.datos;
  if (!datos) {
    return <p className="p-8 text-center text-neutral-500">Cargando impresora…</p>;
  }

  const { impresora } = datos;
  const { enCurso, pendientes, terminadas } = agruparOrdenes(datos.ordenes);
  const config = ESTADOS_IMPRESORA[impresora.estado];

  return (
    <div className="space-y-6">
      <nav aria-label="Miga de pan" className="flex items-center text-neutral-500 text-sm">
        <Link to="/impresoras" className="hover:text-neutral-900">
          Impresoras
        </Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <span className="font-medium text-neutral-900">{impresora.nombre}</span>
      </nav>

      <PageHeader
        titulo={impresora.nombre}
        subtitulo={impresora.modelo ?? undefined}
        alVolver={() => navegar("/impresoras")}
        acciones={
          <Boton
            variante="secundario"
            disabled={detalle.cargando}
            onClick={() => detalle.recargar()}
            // `Boton` es un `button` pelado, sin flex: sin esto el ícono queda
            // ARRIBA del texto en vez de al lado.
            className="inline-flex items-center gap-1.5"
          >
            <RefreshCw className={detalle.cargando ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
            Actualizar
          </Boton>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <EstadoImpresoraBadge estado={impresora.estado} />
          {impresora.tipo ? <Etiqueta>{impresora.tipo}</Etiqueta> : null}
          <Etiqueta tono={impresora.activa ? "ok" : "neutral"}>
            {impresora.activa ? "Activa" : "Dada de baja"}
          </Etiqueta>
        </div>
      </PageHeader>

      {/* El aviso de estado de `PrinterStatusBanner`: la máquina apagada o en
          mantenimiento no toma trabajo, y eso conviene leerlo antes que la
          lista de OTs que tiene encima. */}
      {impresora.estado === "OFFLINE" || impresora.estado === "MAINTENANCE" ? (
        <Card className={`p-4 ${config.fondo}`}>
          <p className={`font-medium text-sm ${config.texto}`}>
            {impresora.estado === "OFFLINE"
              ? "La impresora está offline: no va a tomar trabajo nuevo hasta que vuelva."
              : "La impresora está en mantenimiento: el trabajo asignado no avanza."}
          </p>
        </Card>
      ) : null}

      <Seccion titulo="Datos">
        <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
          <Dato etiqueta="Modelo">{impresora.modelo ?? "—"}</Dato>
          <Dato etiqueta="Tipo">{impresora.tipo ?? "—"}</Dato>
          <Dato etiqueta="En curso">{enCurso ? enCurso.numero : "—"}</Dato>
          <Dato etiqueta="En cola">{pendientes.length}</Dato>
        </dl>
        {impresora.observaciones ? (
          <div className="border-neutral-200 border-t pt-3 text-sm">
            <p className="text-neutral-500">Observaciones</p>
            <p className="whitespace-pre-wrap text-neutral-900">{impresora.observaciones}</p>
          </div>
        ) : null}
      </Seccion>

      {/* El bloque destacado de `ActiveOTSection`: una máquina hace una cosa a
          la vez, y esa cosa merece más lugar que una fila de tabla. */}
      {enCurso ? (
        <Card className="space-y-2 border-brand-200 bg-brand-50 p-4">
          <h2 className="font-semibold text-brand-800 text-sm">En curso</h2>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <button
                type="button"
                disabled={!can("produccion", "read")}
                onClick={() => navegar(`/produccion/${enCurso.id}`)}
                className="font-mono font-semibold text-brand-700 text-lg hover:underline disabled:no-underline"
              >
                {enCurso.numero}
              </button>
              <p className="text-neutral-600 text-sm">
                {enCurso.producto} · {enCurso.cliente}
              </p>
            </div>
            <Etiqueta tono="aviso">{enCurso.estado}</Etiqueta>
          </div>
        </Card>
      ) : null}

      <Seccion titulo={`En cola (${pendientes.length})`}>
        <TablaDeOrdenes
          ordenes={pendientes}
          vacio="Sin trabajo esperando en esta impresora."
          alAbrir={can("produccion", "read") ? (o) => navegar(`/produccion/${o.id}`) : undefined}
        />
      </Seccion>

      <Seccion titulo={`Terminadas (${terminadas.length})`}>
        <TablaDeOrdenes
          ordenes={terminadas}
          vacio="Todavía no se cerró ninguna OT en esta impresora."
          alAbrir={can("produccion", "read") ? (o) => navegar(`/produccion/${o.id}`) : undefined}
        />
      </Seccion>
    </div>
  );
}

function TablaDeOrdenes({
  ordenes,
  vacio,
  alAbrir,
}: {
  ordenes: readonly OrdenDeImpresora[];
  vacio: string;
  /**
   * `| undefined` explícito: con `exactOptionalPropertyTypes` una prop opcional
   * NO acepta que le pasen `undefined`, y acá se pasa a propósito — quien no
   * tiene `produccion.read` no puede abrir la OT.
   */
  alAbrir?: ((orden: OrdenDeImpresora) => void) | undefined;
}) {
  if (ordenes.length === 0) {
    return <p className="text-neutral-500 text-sm">{vacio}</p>;
  }
  return (
    <Tabla cabeceras={["OT", "Producto", "Cliente", "Estado"]}>
      {ordenes.map((o) => (
        <tr
          key={o.id}
          className={alAbrir ? "cursor-pointer hover:bg-neutral-50" : undefined}
          onClick={alAbrir ? () => alAbrir(o) : undefined}
        >
          <td className="px-4 py-2 font-mono text-sm">{o.numero}</td>
          <td className="px-4 py-2">{o.producto}</td>
          <td className="px-4 py-2 text-neutral-600">{o.cliente}</td>
          <td className="px-4 py-2">
            <Etiqueta>{o.estado}</Etiqueta>
          </td>
        </tr>
      ))}
    </Tabla>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <Card className="space-y-3 p-4">
      <h2 className="font-semibold text-neutral-900 text-sm">{titulo}</h2>
      {children}
    </Card>
  );
}

function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-neutral-500">{etiqueta}</dt>
      <dd className="font-medium text-neutral-900">{children}</dd>
    </div>
  );
}
